import NoteHtml, { hasNoteContent } from '../../../../../components/common/NoteHtml';
import { resolveInterestNote } from './interestNote';
import { printConfigFor } from './invoiceSettingDefaults';

/**
 * GST "Tax Invoice" format modelled on the ZipGrid-style maintenance bill:
 * a society header band with GSTIN/PAN/TAN, a member detail box, arrears and
 * net-payable computation strips, a Current Charges table grouped into
 * Non-Taxable (A) / Taxable (B) / GST (C), and a notes + footer panel.
 *
 * Consumes the same `invoice` shape as InvoicePrintPage.jsx. Fields that the
 * data model may not yet provide (IRN, virtual A/C, SAC code, PAN/TAN, place of
 * supply) are rendered only when present so the layout degrades gracefully.
 */
const InvoiceTemplateGST = ({ invoice, pageIdx = 0, totalPages = 1, className = '' }) => {
    const config = printConfigFor(invoice);
    const society = invoice.society || {};
    const flat = invoice.flat || {};
    const member = invoice.member_details || {};
    const series = invoice.series || {};
    const items = invoice.items || [];

    const primaryAddress = society.addresses?.find(a => a.is_primary === 1) || society.addresses?.[0] || {};
    const societyAddress = [
        primaryAddress.address_line1,
        primaryAddress.address_line2,
        [primaryAddress.city?.name, primaryAddress.state?.name].filter(Boolean).join(', '),
        primaryAddress.pincode ? `- ${primaryAddress.pincode}` : ''
    ].filter(Boolean).join(', ');

    const ownership = flat.ownerships?.[0] || {};
    const entity = ownership.entity || {};

    const num = (v) => parseFloat(v || 0);
    const itemAmount = (item) => num(item.base_amount || item.system_amount);
    const formatCurrency = (amount) =>
        num(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatDateLong = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatPeriodLong = (start, end) => {
        if (!start) return '';
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const s = new Date(start);
        const startStr = `${months[s.getMonth()]} ${s.getFullYear()}`;
        if (!end) return startStr;
        const e = new Date(end);
        const endStr = `${months[e.getMonth()]} ${e.getFullYear()}`;
        return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
    };

    // ---- Item grouping ----
    const SYSTEM_HEAD_CODES = ['CGST', 'SGST', 'IGST', 'PAR', 'DNR', 'INT', 'RND', 'ADJ'];
    const chargeItems = items.filter(item => !SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));
    const systemItems = items.filter(item => SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));
    const taxItems = systemItems.filter(item => ['CGST', 'SGST', 'IGST'].includes(item.tariffHead?.head_code));
    const arrearItems = systemItems.filter(item => ['PAR', 'DNR', 'INT'].includes(item.tariffHead?.head_code));

    const taxableItems = chargeItems.filter(item => !!item.taxRule);
    const nonTaxableItems = chargeItems.filter(item => !item.taxRule);

    const nonTaxableTotal = nonTaxableItems.reduce((s, i) => s + itemAmount(i), 0); // A
    const taxableTotal = taxableItems.reduce((s, i) => s + itemAmount(i), 0);       // B
    const gstTotal = (taxItems.reduce((s, i) => s + itemAmount(i), 0)) || num(invoice.tax_amount); // C
    const totalCurrentCharges = nonTaxableTotal + taxableTotal + gstTotal + num(invoice.round_off_amount);

    // ---- Arrears / net payable strips ----
    const arrearsPrincipal = arrearItems
        .filter(i => ['PAR', 'DNR'].includes(i.tariffHead?.head_code))
        .reduce((s, i) => s + itemAmount(i), 0) || num(invoice.arrears_amount);
    const arrearsInterest = arrearItems
        .filter(i => i.tariffHead?.head_code === 'INT')
        .reduce((s, i) => s + itemAmount(i), 0);
    const totalArrears = arrearsPrincipal + arrearsInterest;
    const prevBalance = num(invoice.previous_balance ?? invoice.opening_balance ?? totalArrears);
    const previousPayments = num(invoice.previous_payments ?? 0);
    const advanceCnDn = Math.abs(num(invoice.adjustment_amount)) + num(invoice.discount_amount);
    const netPayable = num(invoice.net_payable);
    const currentInterest = num(invoice.interest_amount);
    const netInterest = currentInterest + arrearsInterest;
    const netPrincipal = netPayable - netInterest;

    // ---- Society / member identity ----
    const societyName = society.name || '';
    const societyGstin = society.gstin_no || society.gst_no || '';
    const societyPan = society.pan_no || '';
    const societyTan = society.tan_no || '';
    const societyRegn = society.registration_number || '';

    const memberName = member.name
        || (entity.entity_type === 'I'
            ? `${entity.first_name || ''} ${entity.last_name || ''}`.trim()
            : entity.company_name)
        || '';
    const memberEmail = entity.email || member.email || '';
    const memberPhone = entity.phone || member.phone || '';
    const memberGst = entity.gstin_no || member.gst_no || '';
    const memberAddress = entity.address || '';
    const unitLabel = `${flat.wing?.title ? `${flat.wing.title} - ` : ''}${flat.flat_number || member.flat_no || ''}`;
    const area = flat.carpet_area || member.flat_area;
    const virtualAccount = invoice.virtual_account_no || member.virtual_account_no || entity.virtual_account_no || '';
    const irn = invoice.irn || '';
    const placeOfSupply = invoice.place_of_supply || primaryAddress.state?.gst_state_code || '';
    const buyerStateCode = invoice.buyer_state_code || '';
    const documentNumber = invoice.document_number || `${series.prefix || ''}${invoice.invoice_id || ''}`;
    const periodText = formatPeriodLong(invoice.period_start, invoice.period_end);
    const sacOf = (item) => item.tariffHead?.sac_code || item.sac_code || '';

    // Formatted Bill Note (stored as HTML by the editor); honours Show Invoice Note.
    const noteHtml = config.show_invoice_note === 1 && hasNoteContent(config.invoice_notes) ? config.invoice_notes : '';
    const interestNote = resolveInterestNote(config, netInterest);

    const RED = '#c1272d';

    // ---- small presentational helpers ----
    const BoxLabel = ({ children }) => (
        <div style={{ fontSize: '8px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{children}</div>
    );
    const Op = ({ children }) => (
        <div style={{ fontSize: '16px', fontWeight: 700, color: '#999', display: 'flex', alignItems: 'center', padding: '0 4px' }}>{children}</div>
    );
    const StripCell = ({ label, value, strong }) => (
        <div style={{ textAlign: 'center', padding: '4px 8px' }}>
            <BoxLabel>{label}</BoxLabel>
            <div style={{ fontSize: strong ? '12px' : '11px', fontWeight: strong ? 800 : 600 }}>{formatCurrency(value)}</div>
        </div>
    );

    return (
        <div
            className={className}
            style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: config.print_font_size ? `${config.print_font_size}px` : '10px', color: '#222', padding: '16px' }}
        >
            {/* ===== Header band ===== */}
            <div style={{ background: RED, color: '#fff', padding: '8px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: '17px', fontWeight: 800 }}>{societyName}</div>
            </div>
            <div style={{ border: '1px solid #ddd', borderTop: 'none', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ maxWidth: '55%' }}>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: RED }}>Maintenance Bill</div>
                    <div style={{ fontSize: '10px', fontWeight: 700 }}>(TAX INVOICE)</div>
                    <div style={{ fontSize: '8px', color: '#888', fontStyle: 'italic' }}>original for recipient</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '9px', lineHeight: 1.5 }}>
                    {societyAddress && <div>{societyAddress}</div>}
                    {societyRegn && <div>Regn No: {societyRegn}</div>}
                    {societyGstin && <div style={{ fontWeight: 700 }}>{societyGstin}</div>}
                    {(societyPan || societyTan) && (
                        <div>
                            {societyPan ? `PAN No. : ${societyPan}` : ''}{societyPan && societyTan ? '   ' : ''}{societyTan ? `TAN No. : ${societyTan}` : ''}
                        </div>
                    )}
                    {periodText && <div style={{ fontWeight: 700, marginTop: '2px' }}>{periodText}</div>}
                </div>
            </div>

            {/* ===== Member box ===== */}
            <div style={{ border: '1px solid #ddd', borderTop: 'none', padding: '8px 14px', fontSize: '9.5px', lineHeight: 1.6 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    <div style={{ width: '60%' }}>
                        <div><span style={{ fontWeight: 700 }}>Member Name :</span> {memberName}</div>
                        {memberAddress && <div><span style={{ fontWeight: 700 }}>Address :</span> {memberAddress}</div>}
                        {memberEmail && <div><span style={{ fontWeight: 700 }}>Email :</span> {memberEmail}</div>}
                        <div><span style={{ fontWeight: 700 }}>Unit :</span> {unitLabel}{area ? `   Area : ${area} Sq. Ft.` : ''}</div>
                    </div>
                    <div style={{ width: '40%' }}>
                        {memberPhone && <div><span style={{ fontWeight: 700 }}>Phone No :</span> {memberPhone}</div>}
                        {config.show_gst_no === 1 && memberGst && <div><span style={{ fontWeight: 700 }}>GST Reg No. :</span> {memberGst}</div>}
                        {virtualAccount && <div><span style={{ fontWeight: 700 }}>Virtual A/C No. :</span> {virtualAccount}</div>}
                    </div>
                </div>
                {irn && <div style={{ wordBreak: 'break-all', marginTop: '2px' }}><span style={{ fontWeight: 700 }}>IRN :</span> {irn}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                    <div><span style={{ fontWeight: 700 }}>RCM Applicable:</span> {invoice.rcm_applicable ? 'Yes' : 'No'}</div>
                    {placeOfSupply && <div><span style={{ fontWeight: 700 }}>Place of Supply:</span> {placeOfSupply}</div>}
                    {buyerStateCode && <div><span style={{ fontWeight: 700 }}>Buyer State Code:</span> {buyerStateCode}</div>}
                </div>
            </div>

            {/* ===== Bill no / dates ===== */}
            <div style={{ border: '1px solid #ddd', borderTop: 'none', padding: '6px 14px', display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: 700 }}>
                <div>Bill No. : {documentNumber}</div>
                <div>Bill Date : {formatDateLong(invoice.bill_date || invoice.invoice_date)}</div>
                <div>Due Date : {formatDateLong(invoice.due_date)}</div>
            </div>

            {/* ===== Arrears strip ===== */}
            <div style={{ border: '1px solid #ddd', borderTop: 'none', padding: '6px 14px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#666', textAlign: 'right' }}>Total Arrears</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                    <StripCell label="Prev. Balance" value={prevBalance} />
                    <Op>−</Op>
                    <StripCell label="Payments" value={previousPayments} />
                    <Op>=</Op>
                    <StripCell label="Total arrears" value={totalArrears} />
                    <Op>{'{'}</Op>
                    <StripCell label="Principal" value={arrearsPrincipal} />
                    <Op>+</Op>
                    <StripCell label="Interest" value={arrearsInterest} />
                    <Op>{'}'}</Op>
                </div>
            </div>

            {/* ===== Net payable strip ===== */}
            <div style={{ border: '1px solid #ddd', borderTop: 'none', padding: '6px 14px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#666', textAlign: 'right' }}>Net Payable</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                    <StripCell label="Total Arrears" value={totalArrears} />
                    <Op>+</Op>
                    <StripCell label="Current Charges" value={totalCurrentCharges} />
                    <Op>−</Op>
                    <StripCell label="Advance/CN/DN" value={advanceCnDn} />
                    <Op>=</Op>
                    <StripCell label="Net Payable" value={netPayable} strong />
                    <Op>{'{'}</Op>
                    <StripCell label="Principal" value={netPrincipal} />
                    <Op>+</Op>
                    <StripCell label="Interest" value={netInterest} />
                    <Op>{'}'}</Op>
                </div>
            </div>

            {/* ===== Body: charges table + notes ===== */}
            <div style={{ display: 'flex', border: '1px solid #ddd', borderTop: 'none' }}>
                {/* Charges */}
                <div style={{ width: '58%', borderRight: '1px solid #ddd' }}>
                    <div style={{ textAlign: 'center', fontWeight: 700, padding: '4px', background: '#f4f4f4', fontSize: '10px' }}>Current Charges</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5px' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 700 }}>Particulars</th>
                                <th style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 700, width: '70px' }}>SAC Code</th>
                                <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 700, width: '90px' }}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Non-taxable */}
                            {nonTaxableItems.length > 0 && (
                                <tr><td colSpan={3} style={{ padding: '3px 8px', fontStyle: 'italic', fontWeight: 700, background: '#fafafa' }}>Non-Taxable</td></tr>
                            )}
                            {nonTaxableItems.map((item, idx) => (
                                <tr key={`nt-${item.item_id || idx}`}>
                                    <td style={{ padding: '3px 8px' }}>{item.tariffHead?.head_name || item.description}</td>
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>{sacOf(item)}</td>
                                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(itemAmount(item))}</td>
                                </tr>
                            ))}
                            {nonTaxableItems.length > 0 && (
                                <tr style={{ borderTop: '1px solid #eee', fontWeight: 700 }}>
                                    <td style={{ padding: '3px 8px' }}>Non Taxable Total A</td>
                                    <td />
                                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(nonTaxableTotal)}</td>
                                </tr>
                            )}

                            {/* Taxable */}
                            {taxableItems.length > 0 && (
                                <tr><td colSpan={3} style={{ padding: '3px 8px', fontStyle: 'italic', fontWeight: 700, background: '#fafafa' }}>Taxable</td></tr>
                            )}
                            {taxableItems.map((item, idx) => (
                                <tr key={`t-${item.item_id || idx}`}>
                                    <td style={{ padding: '3px 8px' }}>{item.tariffHead?.head_name || item.description}</td>
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>{sacOf(item)}</td>
                                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(itemAmount(item))}</td>
                                </tr>
                            ))}
                            {taxableItems.length > 0 && (
                                <tr style={{ borderTop: '1px solid #eee', fontWeight: 700 }}>
                                    <td style={{ padding: '3px 8px' }}>Taxable Total B</td>
                                    <td />
                                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(taxableTotal)}</td>
                                </tr>
                            )}

                            {/* GST */}
                            {taxItems.length > 0 && (
                                <tr><td colSpan={3} style={{ padding: '3px 8px', fontStyle: 'italic', fontWeight: 700, background: '#fafafa' }}>GST Total</td></tr>
                            )}
                            {taxItems.map((item, idx) => (
                                <tr key={`gst-${item.item_id || idx}`}>
                                    <td style={{ padding: '3px 8px' }}>{item.tariffHead?.head_name || item.tariffHead?.head_code}</td>
                                    <td />
                                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(itemAmount(item))}</td>
                                </tr>
                            ))}
                            {gstTotal > 0 && (
                                <tr style={{ borderTop: '1px solid #eee', fontWeight: 700 }}>
                                    <td style={{ padding: '3px 8px' }}>GST Total C</td>
                                    <td />
                                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(gstTotal)}</td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ borderTop: '2px solid #999', fontWeight: 800 }}>
                                <td style={{ padding: '5px 8px' }}>Total Current Charges</td>
                                <td />
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{formatCurrency(totalCurrentCharges)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Notes */}
                <div style={{ width: '42%', padding: '6px 10px' }}>
                    <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '10px', marginBottom: '4px' }}>Notes</div>
                    {noteHtml
                        ? <NoteHtml html={noteHtml} style={{ fontSize: '8.5px', lineHeight: 1.5 }} />
                        : (!interestNote && <div style={{ fontSize: '8.5px', color: '#999' }}>—</div>)}
                    {/* Interest note — configured under Functional Settings. */}
                    {interestNote && (
                        <div style={{ fontSize: '8.5px', lineHeight: 1.5, marginTop: noteHtml ? '4px' : 0 }}>
                            <span style={{ fontWeight: 700 }}>Interest : </span>{interestNote}
                        </div>
                    )}
                </div>
            </div>

            {/* ===== Footer ===== */}
            <div style={{ borderTop: '1px dashed #bbb', marginTop: '10px', paddingTop: '8px', textAlign: 'center', fontSize: '8.5px', color: '#666' }}>
                This is a computer generated bill and receipt, therefore no signature is required.
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <div>
                    <div style={{ fontSize: '8px', color: '#888' }}>Please Pay</div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: RED }}>₹ {formatCurrency(netPayable)} /-</div>
                    <div style={{ fontSize: '8px', color: '#888' }}>Before</div>
                    <div style={{ fontSize: '10px', fontWeight: 700 }}>{formatDateLong(invoice.due_date)}</div>
                </div>
                <div style={{ textAlign: 'center', fontSize: '9px', color: '#888' }}>
                    {totalPages > 1 ? `Page ${pageIdx + 1} of ${totalPages}` : ''}
                </div>
                <div style={{ textAlign: 'right', fontSize: '8px', color: '#888' }} />
            </div>
        </div>
    );
};

export default InvoiceTemplateGST;
